import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Modal,
  Form,
  Switch,
  message,
  Typography,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Drawer,
  Tabs,
  Badge,
  Transfer,
  Alert,
  Steps,
  QRCode,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  MailOutlined,
  UserAddOutlined,
  SafetyOutlined,
  AuditOutlined,
  LockOutlined,
  MobileOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table';
import apiService from '../../services/apiService';
import { useAuthStore } from '../../stores/authStore';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Step } = Steps;

interface User {
  id: string;
  email: string;
  name: string;
  userType: 'ADMIN' | 'SUPER_ADMIN' | 'SALES' | 'CUSTOMER' | 'PICK_SCANNING' | 'PACK_SCANNING' | 'PICKUP_SCANNING' | 'INBOUND_OPERATOR';
  isActive: boolean;
  companyId?: string;
  customerId?: string;
  languagePreference: string;
  createdAt: string;
  updatedAt: string;
  roles: UserRole[];
  twoFactorEnabled?: boolean;
  lastLogin?: string;
  company?: {
    id: string;
    name: string;
  };
  customer?: {
    id: string;
    name: string;
    email?: string;
  };
}

interface UserRole {
  id: string;
  assignedAt: string;
  assignedBy?: string;
  role: Role;
}

interface Role {
  id: string;
  name: string;
  code: string;
  description: string;
  module: string;
  level: number;
  isSystem: boolean;
  permissions: RolePermission[];
}

interface RolePermission {
  permission: Permission;
}

interface Permission {
  id: string;
  name: string;
  module: string;
  action: string;
  resource: string;
  description?: string;
}

interface ApiResponse<T> {
  data: T;
  ok?: boolean;
  message?: string;
}

const UserManagement: React.FC = () => {
  const currentUserRole = useAuthStore(state => state.user?.role);
  const [users, setUsers] = useState<User[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [twoFactorModalVisible, setTwoFactorModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [twoFactorForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('users');
  const [searchText, setSearchText] = useState('');
  // 'ALL' is the unset state for both dropdowns so the table shows everything
  // until the user narrows it.
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [twoFactorSecret, setTwoFactorSecret] = useState<string>('');

  // User statistics
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    admins: 0,
    managers: 0
  });

  // Fetch data
  useEffect(() => {
    fetchUsers();
    fetchCustomers();
  }, []);

  // Filter users by free-text search plus the type / status dropdowns. All three
  // combine (AND), so "ADMIN" + "Inactive" narrows rather than replaces.
  useEffect(() => {
    const term = searchText.trim().toLowerCase();
    const filtered = users.filter(user => {
      const matchesSearch =
        !term ||
        user.name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.userType.toLowerCase().includes(term);

      const matchesType = typeFilter === 'ALL' || user.userType === typeFilter;

      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' ? user.isActive : !user.isActive);

      return matchesSearch && matchesType && matchesStatus;
    });
    setFilteredUsers(filtered);
  }, [users, searchText, typeFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers({
        page: 1,
        limit: 1000 // Get all users for now
      });
      
      if (response.success && response.data) {
        // Map backend user format to frontend format
        const mappedUsers: User[] = response.data.users.map((user: any) => ({
          id: user.id,
          email: user.email,
          name: user.name || '',
          userType: user.role as 'ADMIN' | 'SUPER_ADMIN' | 'SALES' | 'CUSTOMER' | 'PICK_SCANNING' | 'PACK_SCANNING' | 'PICKUP_SCANNING' | 'INBOUND_OPERATOR',
          isActive: user.isActive,
          customerId: user.customerId,
          languagePreference: 'en-US',
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
          roles: [], // Roles will be handled separately if needed
          twoFactorEnabled: false,
          company: user.company
        }));
        
        setUsers(mappedUsers);
        
        // Set statistics from API response
        if (response.data.stats) {
          setUserStats(response.data.stats);
        } else {
          // Calculate statistics if not provided
          const total = mappedUsers.length;
          const active = mappedUsers.filter(u => u.isActive).length;
          // Stat card "Administrators" counts both ADMIN and SUPER_ADMIN so a
          // freshly-promoted super admin doesn't visually drop out of the count.
          const admins = mappedUsers.filter(u => u.userType === 'ADMIN' || u.userType === 'SUPER_ADMIN').length;
          const customers = mappedUsers.filter(u => u.userType === 'CUSTOMER').length;
          setUserStats({ total, active, admins, managers: customers });
        }
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch users');
      console.error('Fetch users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await apiService.getCustomersList();
      if (response.success && response.data) {
        setCustomers(response.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch customers:', error);
      // Don't show error message as it's not critical
    }
  };

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): { valid: boolean; message?: string } => {
    if (password.length < 6) {
      return { valid: false, message: 'Password must be at least 6 characters long' };
    }
    return { valid: true };
  };

  const generatePassword = (): string => {
    const length = 10;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  // User operations
  const handleCreateUser = async (values: any) => {
    try {
      // Validation
      if (!validateEmail(values.email)) {
        message.error('Invalid email format');
        return;
      }

      // Check required fields
      if (!values.name || !values.email || !values.userType) {
        message.error('Required fields missing');
        return;
      }

      // Default password for new users
      const defaultPassword = values.password || 'Welcome@123';

      // Call API to create user
      const response = await apiService.createUser({
        email: values.email,
        password: defaultPassword,
        name: values.name,
        role: values.userType,
        companyId: values.companyId,
        customerId: values.customerId
      });

      if (response.success) {
        message.success('User created successfully');
        setUserModalVisible(false);
        form.resetFields();
        fetchUsers(); // Refresh user list
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to create user');
      console.error('Create user error:', error);
    }
  };

  const handleUpdateUser = async (values: any) => {
    if (!selectedUser) return;
    
    try {
      // Validation
      if (values.email && !validateEmail(values.email)) {
        message.error('Invalid email format');
        return;
      }

      // Call API to update user
      const response = await apiService.updateUser(selectedUser.id, {
        name: values.name,
        email: values.email,
        role: values.userType,
        isActive: values.isActive,
        companyId: values.companyId,
        customerId: values.customerId
      });

      if (response.success) {
        message.success('User updated successfully');
        setUserModalVisible(false);
        setSelectedUser(null);
        form.resetFields();
        fetchUsers(); // Refresh user list
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to update user');
      console.error('Update user error:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const response = await apiService.deleteUser(userId);
      
      if (response.success) {
        message.success('User deactivated successfully');
        fetchUsers(); // Refresh user list
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to delete user');
      console.error('Delete user error:', error);
    }
  };

  const handlePasswordChange = async (values: any) => {
    if (!selectedUser) return;

    try {
      const passwordValidation = validatePassword(values.newPassword);
      if (!passwordValidation.valid) {
        message.error(passwordValidation.message);
        return;
      }

      if (values.newPassword !== values.confirmPassword) {
        message.error('Passwords do not match');
        return;
      }

      // Call API to reset password (admin function)
      const response = await apiService.resetUserPassword({
        email: selectedUser.email,
        newPassword: values.newPassword
      });

      if (response.success) {
        message.success('Password updated successfully');
        setPasswordModalVisible(false);
        passwordForm.resetFields();
        setSelectedUser(null);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to update password');
      console.error('Password change error:', error);
    }
  };

  const handleToggle2FA = async (user: User, enable: boolean) => {
    try {
      if (enable) {
        // Generate mock 2FA setup
        const secret = 'JBSWY3DPEHPK3PXP'; // Mock secret
        const qrUrl = `otpauth://totp/WYNI%20AI%20ERP:${user.email}?secret=${secret}&issuer=WYNI%20AI%20ERP`;
        setTwoFactorSecret(secret);
        setQrCodeUrl(qrUrl);
        setSelectedUser(user);
        setTwoFactorModalVisible(true);
      } else {
        // Disable 2FA
        const updatedUsers = users.map(u => 
          u.id === user.id ? { ...u, twoFactorEnabled: false } : u
        );
        setUsers(updatedUsers);
        message.success('2FA disabled successfully');
      }
    } catch (error) {
      message.error('Failed to toggle 2FA');
    }
  };

  const handleEnable2FA = async (values: any) => {
    if (!selectedUser) return;

    try {
      if (!values.verificationCode || values.verificationCode.length !== 6) {
        message.error('Invalid verification code');
        return;
      }

      // Mock verification (in real app, verify the TOTP code)
      const updatedUsers = users.map(u => 
        u.id === selectedUser.id ? { ...u, twoFactorEnabled: true } : u
      );
      setUsers(updatedUsers);
      message.success('2FA is enabled, and login requires additional verification.');
      setTwoFactorModalVisible(false);
      twoFactorForm.resetFields();
      setSelectedUser(null);
    } catch (error) {
      message.error('Invalid verification code and prevents 2FA enablement.');
    }
  };

  // Role operations

  // Permission check function
  const hasPermission = (user: User, permission: string): boolean => {
    // Check if user has admin role
    if (user.userType === 'ADMIN') return true;
    
    // Check user's roles for the permission
    return user.roles.some(userRole => 
      userRole.role.permissions.some(rp => rp.permission.name === permission)
    );
  };

  // Access control check
  const checkAccess = (user: User, module: string): boolean => {
    if (user.userType === 'ADMIN') return true;
    return user.roles.some(userRole => userRole.role.module === module || userRole.role.module === 'system');
  };

  // Table columns
  const userColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: User) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {name}
            {record.twoFactorEnabled && (
              <SafetyOutlined style={{ marginLeft: 8, color: '#52c41a' }} title="2FA Enabled" />
            )}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.email}</Text>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (type: string) => {
        let color = 'default';
        let displayText = type;
        
        switch(type) {
          case 'SUPER_ADMIN':
            color = 'magenta';
            displayText = 'Super Admin';
            break;
          case 'ADMIN':
            color = 'red';
            displayText = 'Admin';
            break;
          case 'SALES':
            color = 'cyan';
            displayText = 'Sales';
            break;
          case 'CUSTOMER':
            color = 'blue';
            displayText = 'Customer';
            break;
          case 'PICK_SCANNING':
            color = 'green';
            displayText = 'Pick Scanning';
            break;
          case 'PACK_SCANNING':
            color = 'orange';
            displayText = 'Pack Scanning';
            break;
          case 'PICKUP_SCANNING':
            color = 'purple';
            displayText = 'Pickup Scanning';
            break;
        }
        
        return <Tag color={color}>{displayText}</Tag>;
      },
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer: any) => (
        customer ? (
          <div>
            <div style={{ fontWeight: 500 }}>{customer.name}</div>
            {customer.email && (
              <Text type="secondary" style={{ fontSize: '12px' }}>{customer.email}</Text>
            )}
          </div>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (lastLogin: string) => (
        <Text type="secondary">
          {lastLogin ? new Date(lastLogin).toLocaleDateString() : 'Never'}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: User) => (
        <Space>
          <Tooltip title="Edit User">
            <Button 
              type="link" 
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedUser(record);
                form.setFieldsValue({
                  ...record,
                  roleIds: record.roles.map(ur => ur.role.id)
                });
                setUserModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Change Password">
            <Button 
              type="link" 
              icon={<LockOutlined />}
              onClick={async () => {
                setSelectedUser(record);
                setPasswordModalVisible(true);
                // Fetch and display the decrypted password
                try {
                  const response: any = await apiService.getDecryptedPassword(record.id);
                  if (response.success && response.data?.password) {
                    passwordForm.setFieldsValue({
                      newPassword: response.data.password,
                      confirmPassword: response.data.password
                    });
                    message.info('Current password loaded successfully');
                  }
                } catch (error) {
                  console.error('Failed to get decrypted password:', error);
                  // If failed, generate a new password
                  setTimeout(() => {
                    const newPassword = generatePassword();
                    passwordForm.setFieldsValue({
                      newPassword: newPassword,
                      confirmPassword: newPassword
                    });
                    message.warning('Generated new password');
                  }, 100);
                }
              }}
            />
          </Tooltip>
          {/* A "Manage Roles" button used to sit here. It opened a picker whose
              selection was written to React state and never sent anywhere, so
              the assignment vanished on the next refresh. User Type is what
              grants access. */}
          <Tooltip title={record.twoFactorEnabled ? "Disable 2FA" : "Enable 2FA"}>
            <Button 
              type="link" 
              icon={<MobileOutlined />}
              onClick={() => handleToggle2FA(record, !record.twoFactorEnabled)}
            />
          </Tooltip>
          <Tooltip title="Delete User">
            <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDeleteUser(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // The role tables and their modals used to live here: a Role Name / Module /
  // Level column set, a permissions viewer, and a per-user role assigner. None
  // of it reached the server — the roles were built in the browser from a
  // hard-coded list and assignments were only ever held in React state. Removed
  // along with the screens that opened them.

  // Modal components
  const renderUserModal = () => (
    <Modal
      title={selectedUser ? 'Edit User' : 'Add New User'}
      open={userModalVisible}
      onOk={() => form.submit()}
      onCancel={() => {
        setUserModalVisible(false);
        setSelectedUser(null);
        form.resetFields();
      }}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={selectedUser ? handleUpdateUser : handleCreateUser}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              label="Full Name"
              rules={[{ required: true, message: 'Please enter full name' }]}
            >
              <Input placeholder="Enter full name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>
          </Col>
        </Row>

        {!selectedUser && (
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
            initialValue="Welcome@123"
          >
            <Input.Password placeholder="Enter password (default: Welcome@123)" />
          </Form.Item>
        )}

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="userType"
              label="User Type"
              rules={[{ required: true, message: 'Please select user type' }]}
            >
              <Select placeholder="Select user type">
                {/* SUPER_ADMIN option only visible to current super admins -
                    plain ADMIN can't promote themselves. */}
                {currentUserRole === 'SUPER_ADMIN' && (
                  <Option value="SUPER_ADMIN">Super Admin</Option>
                )}
                <Option value="ADMIN">Admin</Option>
                <Option value="SALES">Sales</Option>
                <Option value="CUSTOMER">Customer</Option>
                <Option value="PICK_SCANNING">Pick Scanning</Option>
                <Option value="PACK_SCANNING">Pack Scanning</Option>
                <Option value="PICKUP_SCANNING">Pickup Scanning</Option>
                <Option value="INBOUND_OPERATOR">Inbound Operator</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="languagePreference"
              label="Language"
              initialValue="en-US"
            >
              <Select>
                <Option value="en-US">English</Option>
                <Option value="zh-TW">繁體中文</Option>
                <Option value="zh-CN">简体中文</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="customerId"
          label="Customer"
        >
          <Select
            placeholder="Select customer (optional)"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const customer = customers.find(c => c.id === option?.value);
              if (!customer) return false;
              const searchText = `${customer.name} ${customer.email || ''}`.toLowerCase();
              return searchText.includes(input.toLowerCase());
            }}
          >
            {customers.map(customer => (
              <Option key={customer.id} value={customer.id}>
                {customer.name} {customer.email ? `(${customer.email})` : ''}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* An "Assign Roles" picker used to sit here. Whatever was chosen in it
            was discarded — the roles it listed were invented in the browser and
            were never saved against the user. User Type above is what decides
            access. */}

        <Form.Item
          name="isActive"
          label="Status"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );

  const renderPasswordModal = () => (
    <Modal
      title={`Change Password - ${selectedUser?.name}`}
      open={passwordModalVisible}
      onOk={() => passwordForm.submit()}
      onCancel={() => {
        setPasswordModalVisible(false);
        setSelectedUser(null);
        passwordForm.resetFields();
      }}
      width={500}
    >
      <Form
        form={passwordForm}
        layout="vertical"
        onFinish={handlePasswordChange}
      >
        <Alert
          message="Password Requirements"
          description="Password must be at least 6 characters."
          type="info"
          style={{ marginBottom: 16 }}
        />
        
        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: 'Please enter new password' },
            { min: 6, message: 'Password must be at least 6 characters' }
          ]}
        >
          <Input.Password placeholder="Enter new password" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm new password" />
        </Form.Item>
      </Form>
    </Modal>
  );

  const renderTwoFactorModal = () => (
    <Modal
      title="Enable Two-Factor Authentication"
      open={twoFactorModalVisible}
      onOk={() => twoFactorForm.submit()}
      onCancel={() => {
        setTwoFactorModalVisible(false);
        setSelectedUser(null);
        twoFactorForm.resetFields();
      }}
      width={600}
    >
      <Form
        form={twoFactorForm}
        layout="vertical"
        onFinish={handleEnable2FA}
      >
        <Steps current={0} style={{ marginBottom: 24 }}>
          <Step title="Scan QR Code" />
          <Step title="Verify Code" />
          <Step title="Complete" />
        </Steps>

        <Alert
          message="Scan with Authenticator App"
          description="Use Google Authenticator, Authy, or similar app to scan this QR code."
          type="info"
          style={{ marginBottom: 16 }}
        />

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {qrCodeUrl && (
            <QRCode 
              value={qrCodeUrl} 
              size={200}
              style={{ margin: '0 auto' }}
            />
          )}
        </div>

        <Form.Item
          name="verificationCode"
          label="Verification Code"
          rules={[
            { required: true, message: 'Please enter verification code' },
            { len: 6, message: 'Code must be 6 digits' }
          ]}
        >
          <Input
            placeholder="Enter 6-digit code from your app"
            maxLength={6}
            style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '4px' }}
          />
        </Form.Item>

        <Alert
          message="Manual Entry"
          description={`Secret Key: ${twoFactorSecret}`}
          type="info"
        />
      </Form>
    </Modal>
  );


  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <SafetyOutlined style={{ marginRight: 8 }} />
          User & Role Management
        </Title>
        <Text type="secondary">Manage users, roles, permissions, and security settings</Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={userStats.total}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Users"
              value={userStats.active}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Administrators"
              value={userStats.admins}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Customers"
              value={userStats.managers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'users',
            label: (
              <span>
                <TeamOutlined />
                Users
              </span>
            ),
            children: (
              <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      onClick={() => setUserModalVisible(true)}
                    >
                      Add User
                    </Button>
                    <Input
                      placeholder="Search users..."
                      prefix={<SearchOutlined />}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                    <Select
                      value={typeFilter}
                      onChange={setTypeFilter}
                      style={{ width: 190 }}
                      // Options come from the users actually loaded rather than a
                      // hardcoded list, so a role added later still appears here.
                      options={[
                        { value: 'ALL', label: 'All types' },
                        ...Array.from(new Set(users.map(u => u.userType)))
                          .sort()
                          .map(t => ({ value: t, label: t.replace(/_/g, ' ') }))
                      ]}
                    />
                    <Select
                      value={statusFilter}
                      onChange={setStatusFilter}
                      style={{ width: 150 }}
                      options={[
                        { value: 'ALL', label: 'All statuses' },
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'INACTIVE', label: 'Inactive' }
                      ]}
                    />
                    {(typeFilter !== 'ALL' || statusFilter !== 'ALL' || searchText) && (
                      <Button
                        onClick={() => { setSearchText(''); setTypeFilter('ALL'); setStatusFilter('ALL'); }}
                      >
                        Clear
                      </Button>
                    )}
                    <span style={{ color: '#888' }}>
                      {filteredUsers.length} of {users.length}
                    </span>
                  </Space>
                </div>
                
                <Table
                  columns={userColumns}
                  dataSource={filteredUsers}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    total: filteredUsers.length,
                    //pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: [10, 20, 50, 100, 500],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
                  }}
                />
              </Card>
            )
          }
          // The "Roles & Permissions" tab used to sit here. It listed sixteen
          // invented roles built in the browser — SALES_REP, HR_MANAGER and so
          // on — none of which existed in the database or granted anything.
          // What a user can reach is decided by their User Type alone, so the
          // tab was removed rather than left to imply otherwise.
        ]}
      />

      {renderUserModal()}
      {renderPasswordModal()}
      {renderTwoFactorModal()}
    </div>
  );
};

export default UserManagement; 